import React, { ReactNode, useMemo } from "react"
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client"
import { ApolloProvider } from "@apollo/client/react"
import { SetContextLink } from "@apollo/client/link/context"
import { getMainDefinition } from "@apollo/client/utilities"
import merge from "lodash/merge"
import { useConfig } from "../../configuration/useConfig"
import { fetchAuthSession } from "aws-amplify/auth"
import { RemoveTypenameFromVariablesLink } from "@apollo/client/link/remove-typename"
import { amplifySubscriptionLink } from "./amplifySubscriptionLink"
import { createErrorLink } from "./createErrorLink"

type Props = {
  children: ReactNode
}

const getAccessToken = async (): Promise<string> =>
  (await fetchAuthSession().then((res) => res.tokens?.accessToken.toString())) ?? ""

const parseCustomerAttribute = (value: string | object): object => {
  switch (typeof value) {
    case "string":
      return parseCustomerAttribute(JSON.parse(value))
    case "object":
      return value
    default:
      return {}
  }
}

const ApolloClientProvider = ({ children }: Props) => {
  const { awsRegion, graphqlApiUrl, rumAppMonitorId, rumIdentityPool } = useConfig().config

  // Build the client once per config. Recreating it on every render would hand out
  // a brand-new InMemoryCache each time and throw away everything already cached.
  const client = useMemo(() => {
    const url: string = graphqlApiUrl

    const removeTypenameLink = new RemoveTypenameFromVariablesLink()

    const httpLink = new HttpLink({ uri: url })

    const authLink = new SetContextLink(async (prevContext) => ({
      headers: {
        ...(prevContext.headers as Record<string, string> | undefined),
        Authorization: await getAccessToken()
      }
    }))

    return new ApolloClient({
      link: ApolloLink.from([
        removeTypenameLink,
        createErrorLink({ rumAppMonitorId, rumIdentityPool, awsRegion }),
        ApolloLink.split(
          ({ query }) => {
            const def = getMainDefinition(query)
            return def.kind === "OperationDefinition" && def.operation === "subscription"
          },
          amplifySubscriptionLink,
          ApolloLink.from([authLink, httpLink])
        )
      ]),
      cache: new InMemoryCache({
        typePolicies: {
          ShippingFeeModificationUpdateData: {
            keyFields: false
          },
          OrderWorkflowRevision: {
            keyFields: ["id", "revision"]
          },
          // Its id is the custom state id and is shared across workflows.
          // Normalizing would let one workflow's filter overwrite another's.
          OrderWorkflowFilterCustomState: {
            keyFields: false
          },
          PaymentProvider: {
            keyFields: false
          },
          Provider: {
            keyFields: false
          },
          QueryConfigManagement: {
            merge: true
          },
          QueryProductManagement: {
            merge: true
          },
          QueryOrderManagement: {
            merge: true
          },
          QueryStockManagement: {
            merge: true
          },
          OrderLineModificationUpdateData: {
            keyFields: false
          },
          OrderLineModificationUpdateDataPrice: {
            keyFields: false
          },
          OrderLineModificationCreateData: {
            keyFields: false
          },
          OrderLineModificationDeleteData: {
            keyFields: false
          },
          OrderLineModification: {
            keyFields: false
          },
          OrderShippingAvardaShippingBroker: {
            merge: true
          },
          ShopperInsightsSearchHitCheckoutItem: {
            keyFields: false
          },
          CompensationPayment: {
            keyFields: false
          },
          Order: {
            fields: {
              shippingAddress: {
                merge: true
              },
              customerAttribute: {
                merge(existing, incoming) {
                  return merge(
                    {},
                    parseCustomerAttribute(existing),
                    parseCustomerAttribute(incoming)
                  )
                }
              }
            }
          }
        }
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
          notifyOnNetworkStatusChange: false
        }
      }
    })
  }, [awsRegion, graphqlApiUrl, rumAppMonitorId, rumIdentityPool])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export { ApolloClientProvider }
